/**
 * Stale / malformed URL classification.
 *
 * A past link-generation bug leaked raw TanStack route templates and doubled
 * paths into crawlable URLs, e.g.:
 *   /blog/$tool/$topic/$slug/blog/f1-fantasy/…          (template leaked)
 *   /f1-fantasy/statistics/drivers/$slug/…/lando-norris{}  (template + {} )
 *   /f1-fantasy/standings/f1-fantasy/standings           (path doubled)
 *   /about/about  /login/login  /blog/blog               (path doubled)
 *
 * The bug is fixed (production emits clean links), but Google still has ~120 of
 * these indexed as 404s. Returning a precise signal deindexes them faster than
 * a generic soft/hard 404:
 *   - pure garbage (template placeholder or `{}`) → 410 Gone (never valid)
 *   - an exactly-doubled path (`/X/X`) → 301 to `/X` (recovers the real page)
 *
 * This is intentionally conservative: it only fires on shapes that can never be
 * a real route (`$`, `{}`) or an exact whole-path duplication, so it can't
 * shadow a legitimate URL.
 */

export type StaleUrlAction =
  | { type: "gone" }
  | { type: "redirect"; to: string };

/** Route-template placeholder (`$slug`, `$topic`, …) or a stringified object. */
function isGarbage(pathname: string): boolean {
  // `$` only appears in URLs when a route template segment leaked; `{}` (raw or
  // percent-encoded) is a stringified empty params object.
  return (
    /(^|\/)\$/.test(pathname) ||
    pathname.includes("{}") ||
    pathname.includes("%7B%7D") ||
    pathname.includes("%7b%7d")
  );
}

/**
 * If `pathname` is exactly two identical halves (`/a/b/a/b`), return the first
 * half (`/a/b`); otherwise null. Locale-agnostic — the doubled crawl URLs were
 * both prefixed and unprefixed.
 */
function collapseDoubledPath(pathname: string): string | null {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length < 2 || segs.length % 2 !== 0) return null;
  const half = segs.length / 2;
  for (let i = 0; i < half; i++) {
    if (segs[i] !== segs[i + half]) return null;
  }
  return `/${segs.slice(0, half).join("/")}`;
}

/**
 * Classify a request path. Returns the action to take, or null for a normal URL
 * that should be routed as usual.
 */
export function classifyStaleUrl(pathname: string): StaleUrlAction | null {
  if (isGarbage(pathname)) return { type: "gone" };
  const collapsed = collapseDoubledPath(pathname);
  if (collapsed && collapsed !== pathname) {
    return { type: "redirect", to: collapsed };
  }
  return null;
}
